import { DatePipe } from '@angular/common';
import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import { tap } from 'rxjs/operators';
import { SocieteModel } from 'src/app/shared/models/simple-types/societe.model';
import { BpLineModel } from 'src/app/shared/models/societe-linked/bp/bp-line.model';
import { BpModel } from 'src/app/shared/models/societe-linked/bp/bp.model';
import { CycleBpModel } from 'src/app/shared/models/societe-linked/bp/cycle-bp.model';
import { FournisseurModel } from 'src/app/shared/models/societe-linked/fournisseur.model';
import { SiteModel } from 'src/app/shared/models/societe-linked/site.model';
import { SocieteService } from 'src/app/shared/services/simple/societe.service';
import { FournisseurService } from 'src/app/shared/services/societe-linked/fournisseur.service';
import { SiteService } from 'src/app/shared/services/societe-linked/site.service';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { CycleBpService } from 'src/app/shared/services/bp/cycle-bp.service';

@Component({
  selector: 'app-pdf-bp',
  templateUrl: './pdf-bp.component.html',
  styleUrls: ['./pdf-bp.component.scss'],
  providers: [DatePipe]
})
export class PdfBpComponent implements OnInit, OnChanges {

  @Input() bp: BpModel;
  site: SiteModel;
  siteLivre: SiteModel;
  societe: SocieteModel;
  fourn: FournisseurModel;
  cycles: CycleBpModel[];
  enabled = false;
  emetteur: string;
  valideurCa1: string;
  valideurFin1: string;
  valideurFin2: string;

  constructor(
    private datepipe: DatePipe,
    private siteService: SiteService,
    private societeService: SocieteService,
    private fournService: FournisseurService,
    private toastrService: ToastrService,
    private cycleBpService: CycleBpService,
  ) { }

  ngOnInit(): void {
  }

  ngOnChanges(): void {
    if (this.bp && this.bp.idSociete && this.bp.codeSite && this.bp.codeFourn) {
      this.societeService.get(this.bp.idSociete).subscribe(societe => {
        const join = [];
        this.societe = societe;
        if (this.bp.codeSite === this.bp.codeSiteLivr) {

          join.push(this.siteService.getFromCodeAndSociete(this.bp.codeSite, societe.id).pipe(tap((site: SiteModel) => {
            this.site = site;
            this.siteLivre = site;
          })));

        } else {

          if (this.bp.codeSiteLivr) {
            join.push(this.siteService.getFromCodeAndSociete(this.bp.codeSiteLivr, societe.id).pipe(tap((siteLivr: SiteModel) => {
              this.siteLivre = siteLivr;
            })));
          }

          join.push(this.siteService.getFromCodeAndSociete(this.bp.codeSite, societe.id).pipe(tap((site: SiteModel) => {
            this.site = site;
          })));

        }

        join.push(this.fournService.getFromCodeAndSociete(this.bp.codeFourn, societe.id).pipe(tap((fourn: FournisseurModel) => {
          this.fourn = fourn;
        })));
        join.push(this.cycleBpService.getLastValideurs(this.bp.id).pipe(tap((cycles: CycleBpModel[]) => {
          this.cycles = cycles;
        })));
        forkJoin(join).subscribe(_ => {
          this.enabled = true;
        });
      });
    }
  }



  /**
   * Formate un objet de type Date en string avec le format dd/MM/yyyy et l'heure HH:mm si souhaité
   * @param date La date à formater
   * @param h Si la date doit aussi montrer l'heure
   * @returns une chaine de caractères qui contient la date formatée.
   */
  private formatDate(date: Date, h?: boolean): string {
    return h ? this.datepipe.transform(date, 'dd/MM/yyyy HH:mm') : this.datepipe.transform(date, 'dd/MM/yyyy');
  }

  getSiret(): string {
    return (this.site.siret1 ?
      (this.site.siret2 ? 'SIRET ' + this.site.siret1 + ' ' + this.site.siret2 : 'SIRET ' + this.site.siret1) : '') +
      this.site.naf ? ' - NAF ' + this.site.naf : '';
  }
  // TODO add adresse siteLivre si diff de Site
  getAdressesiteLivre(siteLivre: SiteModel): any {
    if (!siteLivre || siteLivre.code === this.site.code) {
      return {
        res: undefined,
        res2: undefined,
        res3: undefined
      };
    } else {
      return {
        res: [{ text: siteLivre.adr1 }, { text: ' ' }],
        res2: [{ text: siteLivre.adr2 }, { text: ' ' }],
        res3: [{ text: siteLivre.adr3 }, { text: ' ' }]
      };
    }
  }

  private generateBcHeaders(): any{
    const passedBcs = [];
    const arr = [];
    let txt = '';
    arr.push({ text: ' ' });
    for (const line of this.bp.bpLines) {
      if (line.bcLigne) {
        const bc = line.bcLigne.bc;
        if (!passedBcs.includes(bc.id)){
          passedBcs.push(bc.id);
          txt += ( `  BC n°${bc.code} du ${this.formatDate(bc.dBc)}  `);
        }
      }
    }
    arr.push({text: txt, alignment: 'right', fontSize: 9});
    return arr;
  }

  /**
   * Génère la partie BcLines du PDF à partir des BcLines trouvées dans le BC
   * @param lines Les lignes du BC
   * @returns un tableau utilisable dans le générateur de PDF
   */
  private generateBcLines(lines: BpLineModel[], totalHt: number,  totalTva: number,  totalTtc: number): any[] {
    const arr = [];
    const headerBgColor = '#ccc';
    arr.push([
      {
        text: ' ',
        fillColor: headerBgColor
      },
      {
        bold: true,
        text: 'Désignation',
        fillColor: headerBgColor
      },
      {
        alignment: 'right',
        bold: true,
        text: 'Quantité',
        fillColor: headerBgColor
      },
      {
        alignment: 'right',
        bold: true,
        text: 'PU',
        fillColor: headerBgColor
      },
      {
        alignment: 'right',
        bold: true,
        text: 'TVA',
        fillColor: headerBgColor
      },
      {
        alignment: 'right',
        bold: true,
        text: 'Montant',
        fillColor: headerBgColor
      },
    ]
    );
    for (const line of lines) {
      arr.push([// bcLines[x] n°1/2
        {
          alignment: 'right',
          fontSize: 10,
          text: line.code
        },
        {
          text: line.libelle
        },
        {
          alignment: 'right',
          text: line.qte.toFixed(3),
          fontSize: 10
        },
        {
          alignment: 'right',
          text: line.pu.toFixed(2) + '€',
          fontSize: 10
        },
        {
          alignment: 'right',
          text: line.codeTva
        },
        {
          alignment: 'right',
          text: line.ht.toFixed(2) + '€',
          fontSize: 10
        }
      ],
      );
      arr.push([// bcLines[x] n°2/2
        {
          border: [false, false, false, true],
          alignment: 'right',
          fontSize: 10,
          text: ' '
        },
        {
          border: [false, false, false, true],
          fontSize: 8,
          text: 'CG ' + line.codeCgCharge + '     NA ' + line.codeNa + '     CA1 ' + line.codeCa1 + '     Chantier ' + line.chantier
        },
        {
          border: [false, false, false, true],
          text: ' '
        },
        {
          border: [false, false, false, true],
          text: ' '
        },
        {
          border: [false, false, false, true],
          text: ' '
        },
        {
          border: [false, false, false, true],
          text: ' '
        }
      ]);
    }
    arr.push([ // Ligne HT
      {
        border: [false, false, false, true],
        text: ' '
      },
      {
        border: [false, false, false, true],
        text: ' '
      }, {
        border: [false, false, false, true],
        alignment: 'right',
        bold: true,
        italics: true,
        text: 'Total HT '
      },
      {
        border: [false, false, false, true],
        alignment: 'right',
        text: totalHt.toFixed(2) + '€',
        colSpan: 3
      },
    ]
    );
    arr.push([ // Ligne TVA
      {
        border: [false, false, false, true],
        text: ' '
      },
      {
        border: [false, false, false, true],
        text: ' '
      }, {
        border: [false, false, false, true],
        alignment: 'right',
        bold: true,
        italics: true,
        text: 'Total TVA '
      },
      {
        border: [false, false, false, true],
        alignment: 'right',
        text: totalTva.toFixed(2) + '€',
        colSpan: 3
      },
    ]
    );
    arr.push([ // Ligne TTC
      {
        border: [false, false, false, true],
        text: ' '
      },
      {
        border: [false, false, false, true],
        text: ' '
      }, {
        border: [false, false, false, true],
        alignment: 'right',
        bold: true,
        italics: true,
        text: 'Total TTC '
      },
      {
        border: [false, false, false, true],
        alignment: 'right',
        text: totalTtc.toFixed(2) + '€',
        colSpan: 3
      },
    ]
    );
    return arr;
  }

  print(): void {

    const bp = this.bp;
    const site = this.site;
    const siteLivre = this.siteLivre;
    const fourn = this.fourn;
    const societe = this.societe;
    if (bp.statusNum >= 2) {

      const cbc = this.cycles.find(x => x ? x.statutOld === 'Brouillon' : null);
      this.emetteur = cbc ? `${cbc.user.nom} ${cbc.user.prenom} \n${cbc.user.email}` : '';
      if (bp.statusNum >= 6) {

        const ca1 = this.cycles.find(x => x ? x.statutOld === 'A Valider' : null);
        this.valideurCa1 = ca1 ? `${ca1.user.nom}  ${ca1.user.prenom} le ${this.formatDate(ca1.dAction, true)}` : '';

        const valFin1 = this.cycles.find(x => x ? x.statutOld === 'Validation 1' : null);
        this.valideurFin1 = valFin1 ? `${valFin1.user.nom}  ${valFin1.user.prenom} le ${this.formatDate(valFin1.dAction, true)}` : '';

        const valFin2 = this.cycles.find(x => x ? x.statutOld === 'Validation 2' : null);
        this.valideurFin2 = valFin2 ? `${valFin2.user.nom}  ${valFin2.user.prenom} le ${this.formatDate(valFin2.dAction, true)}` : '';

      }
    }


    if (bp && site && fourn && societe) {
      const bcLines = this.generateBcLines(this.bp.bpLines, bp.ht, bp.tva, bp.ttc);
      const bcs = this.generateBcHeaders();
      const dd = {
        info: {
          title: bp.code
        },
        pageSize: 'A4',
        pageOrientation: 'Portrait', // 'landscape',
        pageMargins: [20, 420, 20, 155],
        footer: (currentPage, pageCount) => {
          let signatures;
          if (currentPage === pageCount) {
            signatures = [{
              table: {
                widths: ['*', '*'],

                body: [
                  [[{ text: 'Emetteur', bold: true }, { text: this.emetteur }],
                  [{ text: 'Valideurs', bold: true }, { text: this.valideurCa1 }
                    , { text: this.valideurFin1 }, { text: this.valideurFin2 }]]]
              }, margin: [20, 0, 20, 0]
            }];
          }
          else {
            signatures = { text: '' };
          }

          return [signatures, {
            style: 'tableFooter',
            layout: 'noBorders',
            margin: [20, 0, 20, 0],
            table: {
              widths: ['*', '*', '*'],
              body: [
                [this.formatDate(new Date(Date.now()), true), // Date du jour formatée avec heure et minute
                { text: ' ' },
                { text: 'page ' + currentPage.toString() + ' / ' + pageCount, alignment: 'right' }],
                [{ text: 'Conditions Générales d\'achat Assainissement et Maintenance France', link: 'https://assainissement-et-maintenance.veolia.fr/conditions-generales-achat' }, { text: '' }, { text: '' }],
                ['', { text: societe.capitalSocial, alignment: 'center' }, ''],
                ['', { text: societe.siren + ' ' + societe.rsc + ' - ' + societe.tvaIntraComm, alignment: 'center' }, '']]
            }
          }];
        },


        header: {
          margin: 20,
          style: 'tableExample',
          columns: [
            {
              table: {
                widths: ['50%', '50%'],

                body: [
                  // tslint:disable-next-line: max-line-length
                  [{ text: ' ' }, { text: 'Le ' + this.formatDate(bp.dBp), alignment: 'right' }],
                  [{ text: bcs, alignment: 'left', fontSize: 8, colSpan: 2 }],
                  [{ text: societe.libelle, style: 'subheader1', bold: true }, { text: ' ' }],
                  [{
                    text: [
                      { text: (siteLivre && siteLivre.adr1 === site.adr1) ? 'Site de facturation/livraison: \n' : 'Site de facturation:\n', bold: true, decoration: 'underline' },
                      { text: site.libelle }, '\n',
                      { text: site.adr1 ? site.adr1 + '\n' : '' },
                      { text: site.adr2 ? site.adr2 + '\n' : '' },
                      { text: site.adr3 ? site.adr3 + '\n' : '' },
                      { text: site.tel ? 'Tel : ' + site.tel + '\n' : '' }, '\n',
                      { text: 'Adresse d\'envoi de la facture : \n', bold: true, decoration: 'underline' },
                      { text: site.adrFact1 ? site.adrFact1 + '\n' : '' },
                      { text: site.adrFact2 ? site.adrFact2 + '\n' : '' },
                      { text: site.adrFact3 ? site.adrFact3 + '\n' : '' }, '\n',
                      {
                        text: (siteLivre && ((siteLivre.adr1 !== site.adr1) ||
                          (siteLivre.adr2 !== site.adr2) ||
                          (siteLivre.adr3 !== site.adr3))) ? 'Site de livraison :\n' : '', bold: true, decoration: 'underline'
                      },
                      { text: (siteLivre && siteLivre.adr1 && siteLivre.adr1 !== site.adr1) ? siteLivre.adr1 + '\n' : '' },
                      { text: (siteLivre && siteLivre.adr2 && siteLivre.adr2 !== site.adr2) ? siteLivre.adr2 + '\n' : '' },
                      { text: (siteLivre && siteLivre.adr3 && siteLivre.adr3 !== site.adr3) ? siteLivre.adr3 + '\n' : '' },
                      { text: (siteLivre && siteLivre.tel && siteLivre.tel !== site.tel) ? 'Tel : ' + siteLivre.tel + '\n' : '' }, '\n',
                    ], fontSize: 10
                  },
                  {
                    text: [
                      '\n\n\n',
                      { text: bp.libelleFourn, style: 'subheader1', bold: true }, '\n',
                      { text: fourn.adr1 ? fourn.adr1 + '\n' : '' },
                      { text: fourn.adr2 ? fourn.adr2 + '\n' : '' },
                      { text: fourn.cp + ' ' + fourn.ville }, '\n',
                      { text: fourn.pays }, '\n',
                      { text: fourn.tel ? 'Tel : ' + fourn.tel + '\n' : '' }
                    ]
                  }],
                  [{
                    text: [{ text: (bp.ht < 0 ? 'DEMANDE D\'AVOIR' : 'BON A PAYER ') + bp.code, style: 'header', bold: true },
                      '   ', // Emplacement du Duplicata ou Brouillon
                    { text: bp.commentaires, fontSize: 8 }
                    ], colSpan: 2
                  }],
                  [
                    {
                      table: {
                        widths: [100, '*'],
                        body: [
                          [{ border: [true, true, false, false], text: 'Fournisseur' },
                          { border: [false, true, true, false], text: `${fourn.code} ${fourn.libelle}`}],
                          [{ border: [true, false, false, false], text: 'Règlement' },
                          { border: [false, false, true, false], text: `${bp.libelleModeRegl}     ${bp.dRegl} ` }],
                          [{ border: [true, false, false, true], text: 'Observation' },
                          { border: [false, false, true, true], text:  bp.commentaires }]
                        ]
                      },
                      colSpan: 2
                    },
                  ]
                ]
              },
              layout: 'noBorders'
            }

          ]
        },
        content: [
          {
            padding: [0, 0, 0, 0],
            margin: [0, 0, 0, 0],
            style: 'tableExample',
            table: {
              widths: [25, '*', 53, 50, 25, 85],
              padding: [0, 0, 0, 0],
              margin: [0, 0, 0, 0],
              headerRows: 1,
              body:
                bcLines
            },
            layout: { defaultBorder: false, }
          },
        ],
        styles: {
          header: {
            fontSize: 18,
            bold: true
          },
          subheader: {
            fontSize: 16,
            bold: true
          },
          tableFooter: {
            fontSize: 10,
            margin: [10, 0, 10, 10]
          },
          tableOpacityExample: {
            fillColor: 'blue',
            fillOpacity: 0.3
          },
          tableHeader: {
            bold: true,
            fontSize: 13,
            color: 'black'
          }
        }

      };
      pdfMake.createPdf(dd).open();
    } else {
      this.toastrService.warning('Veuillez mémoriser les sites ainsi que le fournisseur pour générer une facture');
    }
  }
}

